import { useState, useEffect, useCallback } from 'react'
import { Input } from '@/components/ui'
import { Select } from '@/components/ui'
import { sendBrainMessage } from '@/api/brain'
import type { BrainAgent } from '@/types/brain'

interface MessageComposeProps {
  onClose: () => void
  onSent: () => void
  brainOptions: BrainAgent[]
  initialTo?: string
  initialType?: string
  initialSubject?: string
  initialBody?: string
}

const MESSAGE_TYPES = [
  { value: 'notification', label: 'Notification' },
  { value: 'status_update', label: 'Status Update' },
  { value: 'work_order_update', label: 'Work Order Update' },
  { value: 'bug_report', label: 'Bug Report' },
]

export function MessageCompose({
  onClose,
  onSent,
  brainOptions,
  initialTo = '',
  initialType = 'notification',
  initialSubject = '',
  initialBody = '',
}: MessageComposeProps) {
  const [to, setTo] = useState(initialTo)
  const [type, setType] = useState(initialType)
  const [subject, setSubject] = useState(initialSubject)
  const [body, setBody] = useState(initialBody)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const agentSelectOptions = brainOptions.map((a) => ({
    value: a.agentId,
    label: a.agentId,
  }))

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [handleEscape])

  useEffect(() => {
    setTo(initialTo)
    setType(initialType)
    setSubject(initialSubject)
    setBody(initialBody)
    setSubmitting(false)
    setError(null)
    setSuccess(false)
  }, [initialBody, initialSubject, initialTo, initialType])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!to || !type || !subject) {
      setError('To, Type, and Subject are required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await sendBrainMessage({ to, type, subject, body: body || '{}' })
      setSuccess(true)
      setTimeout(() => {
        onSent()
        onClose()
      }, 800)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-swiss-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Compose message"
    >
      <div
        className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto bg-swiss-white border border-swiss-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-swiss-gray-200">
          <h2 className="text-swiss-h2 font-swiss-sans font-semibold">Compose Message</h2>
          <button
            onClick={onClose}
            className="text-swiss-gray-500 hover:text-swiss-black transition-colors duration-150 cursor-pointer p-1"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="text-swiss-red text-swiss-small px-6 pt-4">{error}</div>
        )}
        {success && (
          <div className="text-swiss-green text-swiss-small px-6 pt-4">Message sent.</div>
        )}

        <form onSubmit={(e) => { void handleSubmit(e) }} className="px-6 py-4 flex flex-col gap-4">
          <Select
            label="To"
            required
            value={to}
            onChange={(e) => setTo(e.target.value)}
            options={agentSelectOptions.length > 0 ? agentSelectOptions : [{ value: '', label: 'No agents found' }]}
          />
          <Select
            label="Type"
            required
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={MESSAGE_TYPES}
          />
          <Input
            label="Subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description"
          />

          <div className="flex flex-col gap-1">
            <label
              htmlFor="msg-body"
              className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500"
            >
              Body
            </label>
            <textarea
              id="msg-body"
              className="w-full min-h-[120px] resize-none border border-swiss-gray-200 p-3 text-swiss-body font-swiss-mono focus:outline-none focus:border-swiss-black transition-colors duration-150"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder='{"message": "..."}'
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting || success}
              className="px-4 py-2 text-[13px] font-medium bg-swiss-black text-swiss-white cursor-pointer hover:bg-swiss-gray-800 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending...' : 'Send'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[13px] font-medium border border-swiss-gray-200 text-swiss-black bg-transparent cursor-pointer hover:border-swiss-black transition-colors duration-150"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
